import { inject } from '@angular/core'
import type { CanActivateFn } from '@angular/router'
import { LinguiService } from '@tocdk/lingui-angular'
import { AuthService } from '../auth/auth.service'
import { BrandService } from '../branding/brand.service'
import { uiLanguageFor } from './locale-persistence.service'

/** The locale key the switcher persists (`app.config.ts` reads it at boot). */
export const STORED_LOCALE_KEY = 'tivedo.locale'

/** The stored browser locale, when it is one the UI offers. */
export function storedUiLocale(): 'da' | 'en' | null {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(STORED_LOCALE_KEY) : null
  return v === 'da' || v === 'en' ? v : null
}

/**
 * Activate a UI locale WITHOUT writing it to storage (FR-050 AC-8.2, FR-052 AC-1.10):
 * the visitor's own stored choice survives a branded page. Never rejects.
 *
 * FR-052 U2: skip only when `locale` is active AND nothing else is loading. The
 * LinguiService constructor starts the stored locale's activation without awaiting
 * it, so while a stored `da` catalogue is still loading `locale()` reads the source
 * `en` — skipping then would let the late `da` win over the brand's `en`. Calling
 * `activate` supersedes the in-flight one (LinguiService keeps only the latest).
 */
export async function activateUiLocale(lingui: LinguiService, locale: 'da' | 'en'): Promise<void> {
  const busy = typeof lingui.loading === 'function' && lingui.loading()
  if (lingui.locale() === locale && !busy) return
  try {
    await lingui.activate(locale)
  } catch {
    // a failed catalogue load keeps the current locale; the page still renders
  }
}

/**
 * FR-050 AC-8.2 — branded pre-login pages (login, forgot / reset password,
 * accept-invitation, the branded not-found) render in the
 * brand's default language, overriding the stored browser locale. Activated
 * before the page renders (no flash), and NOT written to storage: the visitor's
 * own choice is still there on a standard page. The pages show no language
 * switcher. A fail-closed address (lookup error, no brand) keeps today's locale.
 * After login the profile language decides (LocalePersistenceService, AC-8.3).
 */
export const brandLanguageGuard: CanActivateFn = async () => {
  const lingui = inject(LinguiService)
  const brand = inject(BrandService)
  // F1 / COV-6: with a session the profile language decides (AC-8.3), also on a
  // pre-login page opened while signed in — including an app that BOOTED on
  // `/a/<name>/…` and so has not read /me yet (session probe, then /me only if
  // signed in — a signed-out visitor makes no failing request).
  const user = await inject(AuthService).probeSession()
  if (user) {
    const own = uiLanguageFor(user.language)
    if (own) await activateUiLocale(lingui, own)
    return true
  }
  const language = brand.brand()?.defaultLanguage
  if (language === 'da' || language === 'en') await activateUiLocale(lingui, language)
  return true
}

/**
 * FR-050 AC-6.5 — the neutral not-found has no brand, so its language is the
 * stored browser locale, else Danish (legacy's pre-login rule, the AC-8.1 default).
 * Not written to storage either.
 */
export const neutralLanguageGuard: CanActivateFn = async () => {
  await activateUiLocale(inject(LinguiService), storedUiLocale() ?? 'da')
  return true
}
